import { lowerCase, startCase, toNumber } from 'lodash-es';
import { computed, unref } from 'vue';
import { useRoute } from 'vue-router';

import { MESSAGE_TYPE_FILTER_OPTIONS } from '@/app/community/constants/communityFilters';
import { formatUser } from '@/app/community/utils/teams';
import { useCreatorOptions } from '@/app/dashboards/composables/useCreatorOptions';
import { useDashboards } from '@/app/dashboards/composables/useDashboards';
import { useMetricDetails } from '@/app/dashboards/composables/useMetricDetails';
import { useReportCategory } from '@/app/dashboards/composables/useReportCategory';
import { useReportMetaAggregateBy } from '@/app/dashboards/composables/useReportMetaAggregateBy';
import { useReportType } from '@/app/dashboards/composables/useReportType';
import {
  ALL_DATA_TYPES,
  DATA_TYPES,
  MAX_ADS_TABLE_REPORT_ROWS,
  REPORT_ERROR_TOOLTIPS,
  TAG_BASED_DATA_TYPES,
  TOTAL_METRIC_GROUPS,
} from '@/app/dashboards/constants';
import { BREAKDOWN_TYPE, METRIC_PREFIX } from '@/app/dashboards/reportConstants';
import { getAdsPlatformController } from '@/app/dashboards/utils/getAdsPlatformController';
import { formatGroupedByReportData } from '@/app/dashboards/utils/grouped-by-metrics.utils';
import { getMetricMediaType } from '@/app/dashboards/utils/media-types.utils';
import {
  getBrandIdToReportAdCampaign,
  getBreakdownLabelWithFallback,
  getFormattedMetrics,
  parseCreatorId,
} from '@/app/dashboards/utils/report.utils';
import {
  getReportBrandsMetrics,
  getReportChannelMetrics,
  getReportCompetitorAverageMetrics,
  getReportCompetitorsMetrics,
  getReportCreatorsMetrics,
  getReportMediaTypeMetrics,
} from '@/app/dashboards/utils/report-metric.utils';
import {
  LINE_GRAPH_REPORT_TYPES,
  OWNED_LINE_CHART_REPORTS,
  PRODUCT_CATEGORY_TO_REPORT_CATEGORY_MAP,
  REPORT_CATEGORY_TO_FILTER_KEY_MAP,
  REPORTS,
} from '@/app/dashboards/utils/reports.enum';
import {
  extractBrandIdsFromReport,
  extractBrandTagObjectsFromReport,
  extractCreatorIdsFromReport,
  uniqueBrandIdsFromTags,
  uniqueSourceAccountIdsFromReport,
} from '@/app/dashboards/utils/tagging.utils';
import { useCustomMetrics } from '@/app/settings/composables/customMetrics';
import { toolTips } from '@/config';
import { REPORT_PREVIEW_AGGREGATE_BY_TYPES } from '@/models/dashboards/aggregate-type.enum';
import {
  BENCHMARK_TYPES,
  INDUSTRY_FOLLOWER_BUCKETS,
} from '@/models/dashboards/benchmark-type.enum';
import {
  ALL_CHANNELS_KEY,
  CHANNELS,
  isUGCMetric as checkIsUGCMetric,
} from '@/models/dashboards/channels.enum';
import { MEDIA_TYPES } from '@/models/dashboards/media-types.enum';
import { ONLINE_FOLLOWERS_METRIC } from '@/models/dashboards/metrics.constants';
import { useAuthStore } from '@/stores/auth';
import { useCommunityMessageTagsStore } from '@/stores/community-message-tags';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import { useContentTagsStore } from '@/stores/content-tags';
import { useDashboardPermissionsStore } from '@/stores/dashboard-permissions';
import { useDashboardsStore } from '@/stores/dashboards';
import { useDashboardIndustriesStore } from '@/stores/dashboards-industries';
import { useDashboardsSocialListeningStore } from '@/stores/dashboards-social-listening';
import { useSearchStore } from '@/stores/search';
import { getMetricDetails } from '@/utils/metrics';

export function getDefaultLineChartAggregateBy(reportType, meta) {
  if (reportType === LINE_GRAPH_REPORT_TYPES.GRAPH_SINGLE_BRAND_MULTI_MEDIA_TYPE) {
    return 'MEDIA_TYPE';
  }
  if ((meta?.channels?.length ?? 0) > 1) return 'CHANNEL';
  if ((meta?.brand_ids?.length ?? 0) > 1) return 'BRAND';
  return 'TOTAL';
}

/**
 * Responsible for extracting data from dashboard report object.
 * The arguments are refs
 */
export function useReport({ report, reportTypeForMetricDetails = null, loading, preview } = {}) {
  const authStore = useAuthStore();
  const searchStore = useSearchStore();
  const dashboardPermissionsStore = useDashboardPermissionsStore();
  const dashboardsSocialListeningStore = useDashboardsSocialListeningStore();
  const communityMultiBrandsStore = useCommunityMultiBrandsStore();
  const messageTagsStore = useCommunityMessageTagsStore();
  const dashboardsStore = useDashboardsStore();
  const dashboardIndustriesStore = useDashboardIndustriesStore();
  const route = useRoute();
  const { canAccessStatsEngineV2 } = useDashboards();

  const { getCustomReportDetailsFromId } = useCustomMetrics();
  const dashboardId = computed(() => unref(report)?.dashboard_id);
  const reportId = computed(() => unref(report)?.id);
  const reportType = computed(() => unref(report)?.type);
  const reportMeta = computed(() => unref(report)?.meta);
  const reportData = computed(() => unref(report)?.data);
  const reportError = computed(() => unref(report)?.error);
  const reportCategory = computed(() => {
    const category = unref(report)?.product_category;
    return PRODUCT_CATEGORY_TO_REPORT_CATEGORY_MAP[category];
  });
  const visionAiReport = computed(() => unref(report)?.visionAiReport);
  const isExpanded = computed(() => unref(report)?.isExpanded);
  const reportLoading = computed(() => unref(loading));
  const reportPreview = computed(() => unref(preview));

  const metricTypeReportKey = computed(() => {
    const reportTypeForMetric = reportTypeForMetricDetails ?? reportType.value;
    return (
      REPORTS[reportTypeForMetric]?.metricTypeReportKey ?? REPORTS.SINGLE_METRIC.metricTypeReportKey
    );
  });

  const {
    isAdsTotalMetric,
    isMultiMediaTypeReport,
    isBenchmarkReport,
    isDoughnutChart,
    isTotalGroupedMetricReport,
    isAdsTotalGroupedMetricReport,
    isCompetitiveReport,
    isCreatorTotalGroupedMetricReport,
    isCreatorReport,
    isStackedBarChart,
  } = useReportType({
    reportType,
  });

  const { isCommunityReportCategory, isListeningReportCategory } = useReportCategory({
    reportCategory,
  });

  const isReport = computed(() => route?.meta?.report);

  const identityBrands = computed(() => {
    const brands = authStore.identity?.brands || {};
    return Object.keys(brands).map((key) => brands[key]);
  });

  const channels = computed(
    () => reportMeta.value?.channels || [].concat(reportMeta.value?.channel || []),
  );

  const metrics = computed(
    () => reportMeta.value?.metrics || [].concat(reportMeta.value?.metric || []),
  );

  const firstMetric = computed(() => metrics.value?.[0]);
  const reportMetric = computed(() => reportMeta.value?.metric || reportMeta.value?.metrics?.[0]);

  const isOwnedLineChart = computed(
    () =>
      OWNED_LINE_CHART_REPORTS.includes(reportType.value) &&
      !isListeningReportCategory.value &&
      !isCommunityReportCategory.value,
  );
  const hasCustomMetricPrefix = computed(() => {
    const firstMetricValue = reportMeta.value?.metrics?.[0] ?? reportMeta.value?.metric;
    return firstMetricValue?.startsWith?.(METRIC_PREFIX.CUSTOM) ?? false;
  });
  const aggregateBy = computed(() => {
    const explicit = reportMeta.value?.aggregate_by;
    if (explicit || !isOwnedLineChart.value || hasCustomMetricPrefix.value) return explicit;
    return getDefaultLineChartAggregateBy(reportType.value, reportMeta.value);
  });
  const secondaryAggregateBy = computed(() => reportMeta.value?.secondary_aggregate_by);
  const brandIds = computed(() => extractBrandIdsFromReport(unref(report)));
  const brandTags = computed(() => reportMeta.value?.brand_tags || []);
  const creatorTagIds = computed(() => reportMeta.value?.creator_tag_ids || []);
  const { creatorOptions, reportCreatorOptions } = useCreatorOptions({
    channels,
    selectedBrandIds: brandIds,
  });
  const reportCustomName = computed(() => reportMeta.value?.custom_name);

  const creatorIds = computed(() => {
    if (!isCreatorReport.value) return [];
    return extractCreatorIdsFromReport({
      // Also extract the creator ids from the selected creator tags
      report: unref(report),
      accountOptions: creatorOptions.value.creatorTags,
      accountTags: creatorTagIds.value,
      accountKey: 'creator_tag_ids',
      dataType: 'CREATOR',
    });
  });
  const reportCompetitorIds = computed(() => uniqueSourceAccountIdsFromReport(unref(report)));
  const competitorTagsByBrand = computed(() => reportMeta.value?.competitor_tags || {});
  const contentTagIds = computed(() => reportMeta.value?.content_tag_ids || []);
  const brandTagIds = computed(() => reportMeta.value?.brand_tags || []);
  const competitorTagIdsByBrand = computed(() => reportMeta.value?.competitor_tags || {});
  const sourceCampaignIds = computed(() => reportMeta.value?.source_campaign_ids || []);
  const boardFilterIds = computed(() => reportMeta.value?.board_ids || []);
  const campaignFilterIds = computed(() => reportMeta.value?.campaign_ids || []);
  const topicFilterIds = computed(() => reportMeta.value?.listening?.topic_ids || []);
  const topicComparisonFilterIds = computed(
    () => reportMeta.value?.listening?.comparison_topic_ids || [],
  );

  const isMultiBrand = computed(() => brandIds.value?.length > 1 || false);
  const isMultiChannel = computed(() => channels.value?.length > 1 || false);
  const isMultiMetric = computed(() => metrics.value?.length > 1 || false);
  const selectedBrandTags = computed(() => extractBrandTagObjectsFromReport(unref(report)));
  const brandIdsOfSelectedTags = computed(() => uniqueBrandIdsFromTags(selectedBrandTags.value));

  const {
    disableAggregationSort,
    reportIsGroupedByChannel,
    reportIsGroupedByBrand,
    reportIsGroupedByTag,
    reportIsGroupedByGender,
  } = useReportMetaAggregateBy({
    aggregateBy,
  });

  const reportChannels = computed(() => channels.value);

  const firstChannel = computed(() =>
    reportChannels.value.length > 0 ? reportChannels.value[0] : null,
  );
  const singleChannel = computed(() => reportMeta.value?.channel);

  const reportChannel = computed(() => singleChannel.value || firstChannel.value);

  const firstMetricDetails = useMetricDetails({
    metric: firstMetric,
    channels: reportChannels,
    metricReportTypeKey: metricTypeReportKey,
  });

  const isUGCMetric = firstMetricDetails.isUGCMetric;
  const isUGCChannel = firstMetricDetails.isEarnedChannel;

  const reportFilters = computed(() => {
    const filterKey = REPORT_CATEGORY_TO_FILTER_KEY_MAP[reportCategory.value];
    return reportMeta?.value?.[filterKey]?.filters ?? {};
  });

  const reportAdCampaigns = computed(() =>
    brandIds.value.map((id) => {
      let hasAccess = false;
      const campaigns = sourceCampaignIds.value.map((campaignId) => {
        const reportDataCampaign = reportData.value?.[campaignId];
        hasAccess = reportDataCampaign?.user_has_access ?? false;
        return {
          campaignId,
          avatar: reportDataCampaign?.avatar,
          name: reportDataCampaign?.name,
          user_has_access: reportDataCampaign?.user_has_access,
        };
      });
      return {
        id,
        campaigns,
        hasAccess,
      };
    }),
  );

  const reportBrands = computed(() => {
    const formattedReportBrands =
      brandIds.value?.map((id) => {
        const reportBrandData = reportData.value?.[id];

        const identityBrand = identityBrands.value?.find((brand) => brand.id === id);
        if (!identityBrand) {
          // user no longer has access to this brand
          return {
            id,
            user_has_access: false,
          };
        }
        let hasAccess = reportBrandData?.user_has_access;
        if (isAdsTotalMetric.value) {
          const brandIdToReportAdCampaign = getBrandIdToReportAdCampaign(reportAdCampaigns.value);
          hasAccess = brandIdToReportAdCampaign[id].hasAccess;
        }

        const errors = reportBrandData?.errors ? { errors: reportBrandData.errors } : {};
        return {
          id,
          avatar: reportBrandData?.avatar || identityBrand?.avatarUrl,
          name: reportBrandData?.name || identityBrand?.name,
          label: reportBrandData?.label || identityBrand?.label,
          user_has_access: hasAccess,
          backend_permissions: reportBrandData?.permissions,
          permissions: identityBrand?.permissions,
          ...errors,
        };
      }) || [];
    return formattedReportBrands.filter((brand) => Object.keys(brand).length > 0);
  });

  const reportCreators = computed(() => {
    const creators = reportCreatorOptions.value;

    const formattedReportCreators =
      creatorIds.value?.map((id) => {
        const creatorId = parseCreatorId(id);
        const reportCreatorData =
          reportData.value?.[creatorId] ?? creators.find((creator) => creator.value === id);

        if (!reportCreatorData) {
          return {
            id,
            user_has_access: false,
          };
        }

        return {
          id,
          avatar: reportCreatorData.avatar,
          name: reportCreatorData.name || reportCreatorData.label || 'Handle Unavailable',
          label: reportCreatorData.name || reportCreatorData.label,
          followers: reportCreatorData.followers,
          user_has_access: reportCreatorData.user_has_access,
          connection_status: reportCreatorData.connection_status,
          invitation_status: reportCreatorData.invitation_status,
        };
      }) || [];
    return formattedReportCreators.filter((creator) => Object.keys(creator).length > 0);
  });

  const reportCompetitors = computed(() => {
    const source = CHANNELS[reportChannel.value]?.competitiveChannelType;

    const competitors = searchStore.filterCompetitors({
      sourceAccountIds: reportCompetitorIds.value,
      source,
    });
    const formattedReportCompetitors =
      competitors
        .map((competitor) => {
          const sourceAccountId = competitor.sourceAccountId;
          const reportCompetitorData = reportData.value?.[sourceAccountId];
          if (competitor.missing) {
            // user no longer has access to this competitor
            const explicitlyAddedCompetitor =
              reportMeta.value?.competitorSourceAccountIds?.includes(sourceAccountId);
            return {
              id: sourceAccountId,
              user_has_access: false,
              source: explicitlyAddedCompetitor ? source : null,
            };
          }
          return {
            id: sourceAccountId,
            avatar: reportCompetitorData?.avatar || competitor?.avatarUrl,
            name: reportCompetitorData?.name || competitor?.handle,
            label: reportCompetitorData?.name || competitor?.handle,
            source: reportCompetitorData?.source || competitor?.source,
            brandDetails: competitor?.brand_details || competitor?.brandDetails,
            user_has_access: reportCompetitorData?.user_has_access,
            isCompetitor: true,
          };
        })
        ?.filter((competitor) => competitor.source === source) || [];
    return formattedReportCompetitors.filter((competitor) => Object.keys(competitor).length > 0);
  });

  const reportBrandId = computed(() => brandIds.value[0] || brandIdsOfSelectedTags.value[0]);

  const reportBrandIds = computed(
    () => reportMeta.value?.brand_ids || brandIdsOfSelectedTags.value || [],
  );

  const reportBenchmarksData = computed(() => {
    const reportDataIds = Object.keys(reportData.value);
    return reportDataIds
      .filter((itemId) => reportData.value[itemId]?.data_type === DATA_TYPES.BENCHMARK)
      .map((itemId) => {
        const benchmarkData = reportData.value[itemId] || {};
        const benchmarkDataId = itemId || '';
        return { ...benchmarkData, benchmarkDataId };
      });
  });

  const availableAssignees = computed(() => {
    const allAssignees = new Map();

    reportBrandIds.value?.forEach((brandId) => {
      const brandAssignees =
        communityMultiBrandsStore.brandIdToAccessibleBrandUsersMap[brandId] || [];

      brandAssignees.forEach((user) => {
        const formattedUser = formatUser(user);
        if (!allAssignees.has(formattedUser.value)) {
          const hasValidName = !!(user.firstName?.trim() || user.lastName?.trim());
          if (!hasValidName) {
            formattedUser.label = user.email;
          }
          allAssignees.set(formattedUser.value, formattedUser);
        }
      });
    });

    return allAssignees;
  });

  const allAvailableMessageTags = computed(() => {
    const tagsByBrand = messageTagsStore.messageTagsByBrand;
    return Object.values(tagsByBrand).flat();
  });

  const dataTypeToDataMap = computed(() => ({
    [ALL_DATA_TYPES.ASSIGNEE]: (key) => {
      const assigneeId = toNumber(key.replace('ASSIGNEE_', ''));
      if (assigneeId === -1) {
        return { name: 'Unassigned' };
      }
      const assignee = availableAssignees.value.get(assigneeId);
      if (assignee) return { avatar: assignee?.avatarUrl, name: assignee.label };
      return { avatar: null, error: true, name: REPORT_ERROR_TOOLTIPS.UNAVAILABLE_ASSIGNEE };
    },
    [ALL_DATA_TYPES.MESSAGE_TAG]: (key) => {
      const messageTagId = toNumber(key.replace('MESSAGE_TAG_', ''));
      const messageTag = allAvailableMessageTags.value.find(
        (tagItem) => tagItem.id === messageTagId,
      );

      if (messageTag) return { leadingChip: { icon: 'tag' }, name: messageTag.name };
      return { name: getBreakdownLabelWithFallback(messageTagId, BREAKDOWN_TYPE.MESSAGE_TAG) };
    },
    [DATA_TYPES.CONTENT_TAG]: () => ({
      leadingChip: { icon: 'tag' },
    }),
    [ALL_DATA_TYPES.MEDIA_TYPE]: (key) => {
      // Normalize key: 'MEDIA_TYPE_carousel' -> 'CAROUSEL', 'image' -> 'PHOTO'
      let mediaTypeKey = key.replace('MEDIA_TYPE_', '').replace(':', '_').toUpperCase();
      if (mediaTypeKey === 'IMAGE') {
        mediaTypeKey = 'PHOTO';
      }
      const mediaType = MEDIA_TYPES[mediaTypeKey];
      return {
        leadingChip: { icon: mediaType?.gdiIcon ?? 'mixed' },
      };
    },
    [ALL_DATA_TYPES.TOPIC]: (key) => {
      const topicId = toNumber(key.replace('TOPIC_', ''));
      const topic = dashboardsSocialListeningStore.topicListForMultipleOrganizations?.find(
        (topicItem) => topicItem.id === topicId,
      );
      if (topic) return { name: topic.name };
      return { name: getBreakdownLabelWithFallback(topicId, BREAKDOWN_TYPE.TOPIC) };
    },
    [ALL_DATA_TYPES.MESSAGE_TYPE]: (key) => {
      const messageOption = Object.values(MESSAGE_TYPE_FILTER_OPTIONS).find(
        (option) => option.statsValue === key,
      );
      if (messageOption) return { name: messageOption.label };
      return { name: getBreakdownLabelWithFallback(key, BREAKDOWN_TYPE.MESSAGE_TYPE) };
    },
    [ALL_DATA_TYPES.INDUSTRY]: (key) => {
      const industryName = key.replace('INDUSTRY_', '');
      if (industryName) return { name: industryName };
      return { name: getBreakdownLabelWithFallback(null, BREAKDOWN_TYPE.INDUSTRY) };
    },
    [ALL_DATA_TYPES.CHANNEL]: (key) => ({
      name: getBreakdownLabelWithFallback(key, 'channel'),
    }),
    [ALL_DATA_TYPES.AGE]: (key) => ({
      name: key,
    }),
  }));

  function formatReportData(data) {
    return Object.entries(data).reduce((acc, [key, value]) => {
      const reportDetails = dataTypeToDataMap.value?.[value.data_type]?.(key);
      const name = reportDetails?.name ?? value?.name ?? startCase(key.toLowerCase());

      acc[key] = {
        ...value,
        ...reportDetails,
        name,
        ...(value.breakdowns && { breakdowns: formatReportData(value.breakdowns) }), // format the breakdowns data
      };
      return acc;
    }, {});
  }

  const formattedReportDataForTableAndGraphReports = computed(() =>
    Object.fromEntries(
      Object.entries(formatReportData(reportData.value)).sort(([a], [b]) => a.localeCompare(b)),
    ),
  );

  const reportMetricDetails = computed(() =>
    getMetricDetails(metrics.value[0], reportChannels.value, metricTypeReportKey.value),
  );

  function metricValueDictHasData(valueDict) {
    const hasValue = valueDict?.value !== null && valueDict?.value !== undefined;
    const hasContext = valueDict?.context !== null && valueDict?.context !== undefined;
    return hasValue && hasContext;
  }

  const lineGraphReportHasData = computed(() => {
    const validReportTypes = [REPORTS.GRAPH.value, REPORTS.COMPETITIVE_GRAPH.value];
    if (!validReportTypes.includes(reportType.value)) return false;

    return Object.values(reportData.value).some((accountData) =>
      reportChannels.value.some((channel) =>
        metrics.value.some((metric) => {
          const valueDict = accountData?.[channel]?.[metric] || {};
          return Object.values(valueDict)?.length > 0;
        }),
      ),
    );
  });

  function metricsObjectHasData(metricsObject) {
    const channelsToCheck = [...reportChannels.value, ALL_CHANNELS_KEY];
    return metrics.value.some((metric) =>
      channelsToCheck.some((channel) => {
        const valueDict = metricsObject?.[metric]?.[channel] || {};
        return metricValueDictHasData(valueDict);
      }),
    );
  }

  const brandAggregateMetricReportHasData = computed(() => {
    const brandAggregateReportTypes = [
      REPORTS.COMPETITIVE_MULTI_BRAND_METRIC.value,
      REPORTS.MULTI_METRIC_MEDIA_TYPE.value,
      REPORTS.SINGLE_METRIC.value,
      REPORTS.MULTI_BRAND_METRIC.value,
    ];
    if (
      aggregateBy.value !== TOTAL_METRIC_GROUPS.BRAND &&
      !brandAggregateReportTypes.includes(reportType.value)
    )
      return false;

    return Object.values(reportData.value).some((accountData) => {
      const metricObj = accountData?.metrics || {};
      return metricsObjectHasData(metricObj);
    });
  });

  const otherAggregateMetricReportHasData = computed(() => {
    const rootLevelAggregates = [TOTAL_METRIC_GROUPS.TOTAL, TOTAL_METRIC_GROUPS.CHANNEL];
    if (!rootLevelAggregates.includes(aggregateBy.value)) return false;

    const metricObj = reportData.value?.metrics || {};
    return metricsObjectHasData(metricObj);
  });

  const reportHasData = computed(
    () =>
      lineGraphReportHasData.value ||
      brandAggregateMetricReportHasData.value ||
      otherAggregateMetricReportHasData.value,
  );

  const isMultiBrandOrCompetitiveReport = computed(() => {
    const hasMultipleBrandsOrCompetitors =
      reportBrands.value.length + reportCompetitors.value.length > 1;
    const hasBrandTags = brandTags.value.length;
    const hasCompetitorTags = Object.keys(competitorTagsByBrand.value).length;

    return Boolean(hasMultipleBrandsOrCompetitors || hasBrandTags || hasCompetitorTags);
  });

  const isInstagramReport = computed(
    () => reportChannels.value.length === 1 && reportChannels.value[0] === CHANNELS.INSTAGRAM.value,
  );

  const isFacebookReport = computed(
    () => reportChannels.value.length === 1 && reportChannels.value[0] === CHANNELS.FACEBOOK.value,
  );

  const isFacebookCompetitiveReport = computed(
    () =>
      reportChannels.value.length === 1 &&
      reportChannels.value[0] === CHANNELS.FACEBOOK_COMPETITIVE.value,
  );

  const isInstagramCompetitiveReport = computed(
    () =>
      reportChannels.value.length === 1 &&
      reportChannels.value[0] === CHANNELS.INSTAGRAM_COMPETITIVE.value,
  );

  const hasReportMetrics = computed(() => metrics.value.length > 0);

  const hasMediaSubtypes = computed(
    () =>
      hasReportMetrics.value &&
      metrics.value.every(
        (metric) => !!getMetricMediaType(reportChannel.value, metric, metricTypeReportKey.value),
      ),
  );

  const hasUGCMetrics = computed(() => metrics.value.every((metric) => checkIsUGCMetric(metric)));

  const isMonetaryMetric = firstMetricDetails.isMonetaryMetric;

  const isTimeSeriesReport = computed(
    () =>
      Object.keys(LINE_GRAPH_REPORT_TYPES).includes(reportType.value) ||
      (isStackedBarChart.value && firstMetric.value === ONLINE_FOLLOWERS_METRIC),
  );

  const currency = computed(() => {
    const adsPlatformController = getAdsPlatformController(reportChannel.value);
    if (!isMonetaryMetric.value || !adsPlatformController) return '';

    // Find the first accountId that exists in the account -> currency map
    const accountToCurrency = adsPlatformController.adAccountToCurrency.value;
    const accountId = reportMeta.value?.source_ad_account_ids?.find((id) => accountToCurrency[id]);
    return accountToCurrency[accountId] ?? 'USD';
  });

  const displayStrategy = computed(() => reportMeta.value.display_strategy);

  const reportMediaTypeMetrics = computed(() =>
    getReportMediaTypeMetrics(
      metrics.value,
      reportData.value,
      reportBrands.value,
      reportBrandId.value,
      reportChannel.value,
      metricTypeReportKey.value,
    ),
  );

  const reportBrandsMetrics = computed(() =>
    getReportBrandsMetrics(
      metrics.value,
      reportData.value,
      reportBrands.value,
      sourceCampaignIds.value,
      reportChannel.value,
      isAdsTotalMetric.value,
    ),
  );

  const reportCreatorMetrics = computed(() =>
    getReportCreatorsMetrics(
      metrics.value,
      reportData.value,
      reportCreators.value,
      reportChannel.value,
    ),
  );

  const reportCompetitorsMetrics = computed(() =>
    getReportCompetitorsMetrics(
      metrics.value,
      reportData.value,
      reportCompetitors.value,
      reportChannel.value,
    ),
  );

  const reportCompetitorAverageMetrics = computed(() =>
    getReportCompetitorAverageMetrics(metrics.value, reportData.value, reportChannel.value),
  );

  const reportBenchmarksMetrics = computed(() => {
    const metaBenchmarks = reportMeta.value?.benchmarks || [];
    const isTsi = metaBenchmarks.some((b) => b.type === BENCHMARK_TYPES.INDUSTRY);

    if (isTsi) {
      return buildTsiBenchmarkRows(metaBenchmarks);
    }

    return reportBenchmarksData.value.map((benchmark) => ({
      ...benchmark,
      ...formatBenchmarkRow(benchmark?.metrics || {}),
    }));
  });

  function buildTsiBenchmarkRows(metaBenchmarks) {
    const industryOptions = dashboardIndustriesStore.hubspotIndustryOptions;

    return metaBenchmarks
      .filter((b) => b.type === BENCHMARK_TYPES.INDUSTRY)
      .map((metaBenchmark) => {
        const industryOption = industryOptions?.find(
          (opt) =>
            opt.id === metaBenchmark.industry_id &&
            (opt.follower_bucket_id === metaBenchmark.follower_bucket ||
              opt.follower_bucket === metaBenchmark.follower_bucket),
        );

        if (!industryOption) return null;

        const industryEntries = reportBenchmarksData.value.filter((entry) =>
          entry.benchmarkDataId?.startsWith(`benchmarks_${metaBenchmark.industry_id}_`),
        );

        const responseEntry =
          industryEntries.find(
            (entry) => entry.follower_bucket === industryOption.follower_bucket,
          ) || (industryEntries.length === 1 ? industryEntries[0] : undefined);

        const rawBucket = responseEntry?.follower_bucket || industryOption.follower_bucket;

        return {
          ...responseEntry,
          ...formatBenchmarkRow(responseEntry?.metrics || {}),
          name: responseEntry?.name || industryOption.label?.split(': ')[0],
          follower_bucket: INDUSTRY_FOLLOWER_BUCKETS[rawBucket]?.text || rawBucket,
          data_type: DATA_TYPES.BENCHMARK,
          benchmark_type: metaBenchmark.type,
        };
      })
      .filter(Boolean);
  }

  function formatBenchmarkRow(benchmarkMetrics) {
    const benchmarkChannel =
      Object.keys(benchmarkMetrics?.[metrics.value[0]] || {})[0] || reportChannel.value;
    const { formattedMetrics, metric } = getFormattedMetrics(
      benchmarkMetrics,
      metrics.value[0],
      benchmarkChannel,
    );
    return { metric, formattedMetrics };
  }

  /**
   * @description
   * This computed property is used to get the data by breakdown.
   * It maps the report data to an array of objects with the report key and the item.
   * It filters the data by the aggregate by. So for sentiment report, it will filter the data to only return
   * the data object with the data_type of sentiment.
   */
  const dataByBreakdown = computed(() =>
    Object.entries(formattedReportDataForTableAndGraphReports.value)
      .map(([key, item]) => ({
        reportKey: key,
        ...item,
      }))
      .filter((item) => lowerCase(item.data_type) === lowerCase(aggregateBy.value)),
  );

  function keyIsInFilters(key) {
    const aggregateInfo = REPORT_PREVIEW_AGGREGATE_BY_TYPES?.[aggregateBy.value];
    if (!aggregateInfo) return false;

    if (aggregateInfo.filterKey) {
      if (aggregateInfo.keyIdGetter)
        return reportFilters.value?.[aggregateInfo.filterKey]?.includes(
          aggregateInfo.keyIdGetter(key),
        );
      return reportFilters.value?.[aggregateInfo.filterKey]?.includes(key);
    }
    return false;
  }

  const reportMetricsBrokenDown = computed(() =>
    dataByBreakdown.value.map((item) => {
      const data = item?.metrics;
      const { name, avatar, data_type } = item;
      const isBrand = data_type === DATA_TYPES.BRAND;

      if (!data) {
        // isBrand not needed here
        if (keyIsInFilters(item.reportKey))
          return {
            ...item,
            user_has_access: true,
            metric: {},
            name,
            avatar,
          };
        return {
          ...item,
          user_has_access: isBrand ? (item?.permissions?.user?.can_access_brand ?? true) : false,
          metric: {},
          name,
          avatar,
          isBrand,
        };
      }
      let channelKey = ALL_CHANNELS_KEY;
      if (aggregateBy.value === REPORT_PREVIEW_AGGREGATE_BY_TYPES.CHANNEL.value)
        channelKey = item.reportKey;
      const { formattedMetrics, metric } = getFormattedMetrics(data, metrics.value[0], channelKey);

      return {
        ...item,
        metric,
        formattedMetrics,
        user_has_access: true,
        isBrand,
      };
    }),
  );

  const reportChannelsMetrics = computed(() =>
    getReportChannelMetrics(metrics.value, reportData.value, reportChannels.value),
  );

  const reportBrandAggregated = computed(() =>
    reportBrands.value?.map((brand) => {
      const reportDataBrand = reportData.value?.[brand.id];

      if (!reportDataBrand) {
        return {
          ...brand,
        };
      }

      const { metrics: brandMetrics = {}, hidden_metrics: hiddenMetrics = false } = reportDataBrand;
      const { formattedMetrics, metric } = getFormattedMetrics(brandMetrics, metrics.value[0]);
      return {
        ...brand,
        hiddenMetrics,
        isBrand: true,
        metric,
        formattedMetrics,
      };
    }),
  );

  // Filter report data to only include items with TAG or ACCOUNT_TAG data_type
  const reportTagAggregated = computed(() => {
    if (!reportData.value) return [];

    return Object.entries(reportData.value)
      .filter(([, item]) => TAG_BASED_DATA_TYPES.includes(item?.data_type))
      .map(([id, item]) => {
        const tagMetrics = item?.metrics || {};
        const { formattedMetrics, metric } = getFormattedMetrics(tagMetrics, metrics.value[0]);
        return {
          id, // Keep as string - keys like 'BRAND_TAG_55' would produce NaN with toNumber
          name: item.name,
          metric,
          formattedMetrics,
        };
      });
  });

  const reportCreatorAggregated = computed(() => {
    const { connectedAccountsOnly } = useMetricDetails({
      metric: firstMetric.value,
      channels: reportChannels.value,
      metricReportTypeKey: metricTypeReportKey.value,
    });
    const rows = reportCreators.value?.map((creator) => {
      const creatorId = parseCreatorId(creator.id);
      const reportDataCreator = reportData.value?.[creatorId];

      if (!reportDataCreator) {
        return {
          ...creator,
        };
      }

      const creatorMetrics = reportDataCreator?.metrics || {};
      const { formattedMetrics, metric } = getFormattedMetrics(creatorMetrics, metrics.value[0]);

      return {
        ...creator,
        isCreator: true,
        metric,
        formattedMetrics,
      };
    });
    if (connectedAccountsOnly.value)
      return rows.filter((row) => dashboardPermissionsStore.creatorIsConnected(row));
    return rows;
  });

  const reportGroupedByMetrics = computed(() =>
    formatGroupedByReportData(
      reportChannel.value,
      reportData.value,
      metrics.value[0],
      aggregateBy.value,
    ),
  );

  const reportMetricNumbers = computed(() => reportBrandsMetrics.value?.[0]?.metric);

  const reportSortOrder = computed(() => {
    const defaultSortOrder = reportMetricDetails.value?.isNegativeMetric ? 'asc' : 'desc';
    return reportMeta.value?.sort_order ?? defaultSortOrder ?? 'desc';
  });

  const showDownloadReportImageButton = computed(() => visionAiReport.value);

  const thumbnailMode = computed(() => {
    if (visionAiReport.value) {
      return !isExpanded.value;
    }

    return false;
  });

  const isCustomMetricReportTile = computed(() =>
    Boolean(!reportPreview.value && firstMetric.value?.includes(METRIC_PREFIX.CUSTOM)),
  );

  const isStoryFrameReport = computed(
    () =>
      firstChannel.value === CHANNELS.INSTAGRAM_STORIES.value &&
      !reportMetricDetails.value.isGalleryMetric,
  );

  const isStoryGalleryReport = computed(
    () =>
      firstChannel.value === CHANNELS.INSTAGRAM_STORIES.value &&
      reportMetricDetails.value.isGalleryMetric,
  );

  const visionAiImageDownloadButtonTooltip = computed(() => toolTips.visionAi.downloadButton);

  const filteredBrands = computed(() =>
    reportBrandsMetrics.value.filter((brandMetrics) => reportPreview.value || brandMetrics.metric),
  );

  const filteredCompetitors = computed(() =>
    reportCompetitorsMetrics.value.filter(
      (competitorMetrics) => reportPreview.value || competitorMetrics.metric,
    ),
  );

  const metricReportCombinedData = computed(() => {
    if (isCommunityReportCategory.value || isListeningReportCategory.value) {
      return [...reportMetricsBrokenDown.value];
    }
    if (isMultiMediaTypeReport.value) {
      // Stats engine v2 uses data_type-based breakdown format (like TOTAL_GROUPED_METRIC)
      // Remove reportMediaTypeMetrics branch when removing canAccessStatsEngineV2 flag
      return canAccessStatsEngineV2.value
        ? [...reportMetricsBrokenDown.value]
        : [...reportMediaTypeMetrics.value];
    }
    if (isCompetitiveReport.value) {
      const combinedCompetitiveAndBrandItems = [
        ...filteredBrands.value,
        ...filteredCompetitors.value,
      ];
      if (
        reportCompetitorAverageMetrics.value &&
        filteredCompetitors.value.filter((competitor) => competitor.user_has_access).length > 1
      ) {
        combinedCompetitiveAndBrandItems.push(reportCompetitorAverageMetrics.value);
      }
      return combinedCompetitiveAndBrandItems;
    }
    if (isBenchmarkReport.value) {
      return [...filteredBrands.value, ...reportBenchmarksMetrics.value];
    }
    if (isDoughnutChart.value) {
      if (reportIsGroupedByChannel.value) {
        return [...reportChannelsMetrics.value];
      }
      if (reportIsGroupedByBrand.value) {
        return reportBrandAggregated.value.filter(
          (brandMetrics) =>
            reportPreview.value ||
            (brandMetrics.metric && Object.keys(brandMetrics.metric).length > 0),
        );
      }
      if (reportIsGroupedByTag.value) {
        return reportTagAggregated.value.filter(
          (tagMetrics) =>
            reportPreview.value || (tagMetrics.metric && Object.keys(tagMetrics.metric).length > 0),
        );
      }
    }
    if (isTotalGroupedMetricReport.value) {
      if (reportIsGroupedByChannel.value) {
        return [...reportChannelsMetrics.value];
      }
      if (reportIsGroupedByBrand.value) {
        return [...reportBrandAggregated.value];
      }
      // TAG aggregation uses special handling because aggregate_by is 'TAG'
      // but data_type is 'ACCOUNT_TAG' or 'TAG'
      if (reportIsGroupedByTag.value) {
        return [...reportTagAggregated.value];
      }
      // For other breakdowns (CONTENT_TAG, etc.), use dataByBreakdown filtering
      if (aggregateBy.value) {
        return [...reportMetricsBrokenDown.value];
      }
    }
    if (isCreatorTotalGroupedMetricReport.value) {
      if (reportIsGroupedByBrand.value) {
        return [...reportCreatorAggregated.value];
      }
    }
    if (isAdsTotalGroupedMetricReport.value) {
      if (reportIsGroupedByBrand.value) {
        return [...reportBrandAggregated.value];
      }
      return [...reportGroupedByMetrics.value];
    }
    return [...filteredBrands.value];
  });

  const sortedCombinedItems = computed(() => {
    const newCombinedItems = metricReportCombinedData.value;

    // Ad age/gender reports are formatted differently and are sorted at this level
    if (disableAggregationSort.value && !reportIsGroupedByGender.value) return newCombinedItems;
    // gender reports should not have their sort order modified
    const sortOrder = reportIsGroupedByGender.value ? 'desc' : reportSortOrder.value;

    // if we are sorting ascending, then we want undefined values sorted last
    const defaultNumber =
      sortOrder === 'desc' ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;

    newCombinedItems.sort((a, b) => {
      const aMetric = a?.metric?.value ?? defaultNumber;
      const bMetric = b?.metric?.value ?? defaultNumber;

      if (aMetric < bMetric) {
        return 1;
      }
      if (aMetric > bMetric) {
        return -1;
      }
      if (a?.name && b?.name) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

    return sortOrder.toLowerCase() === 'asc' ? newCombinedItems.reverse() : newCombinedItems;
  });

  const loadingRows = computed(() =>
    Array.from({ length: 4 }, () => ({
      id: null,
      metric: {},
      metrics: [],
    })),
  );

  const metricReportRows = computed(() => {
    if (reportLoading.value) {
      return loadingRows.value;
    }
    // Performance issue when loading table report with hundreds or thousands of rows
    if (isAdsTotalGroupedMetricReport.value) {
      return sortedCombinedItems.value.slice(0, MAX_ADS_TABLE_REPORT_ROWS);
    }

    return sortedCombinedItems.value;
  });

  const contentTags = computed(() => {
    const contentTagsStore = useContentTagsStore();
    const contentTagIdsSet = new Set(contentTagIds.value);
    return contentTagsStore.allOrganizationsContentTags.filter((contentTag) =>
      contentTagIdsSet.has(contentTag.id),
    );
  });

  const inaccessibleContentTagIds = computed(() => {
    // this includes deleted content tags & content tags that belong to a different org
    const contentTagsStore = useContentTagsStore();
    const allOrgsContentTagIds = new Set(
      contentTagsStore.allOrganizationsContentTags.map((tag) => tag.id),
    );
    return contentTagIds.value.filter((contentTagId) => !allOrgsContentTagIds.has(contentTagId));
  });

  function canAccessData(id) {
    return reportData.value?.[id]?.user_has_access === true;
  }

  const customMetricDetails = computed(() => getCustomReportDetailsFromId(firstMetric.value));

  const customMetricName = computed(() => customMetricDetails.value?.name);

  const customMetricKey = computed(() => customMetricDetails.value?.key);

  const canAccessReportData = computed(() => {
    const canAccessAnyBrandData = reportBrandIds.value.some(
      (brandId) => !!reportData.value?.[brandId]?.user_has_access,
    );
    const canAccessAnyCompetitorData = reportCompetitorIds.value.some(
      (competitorId) => !!reportData.value?.[competitorId]?.user_has_access,
    );

    const canAccessAnyCreatorData = reportCreators.value.some((creator) => creator.user_has_access);
    const canAccessAnyListeningData = isListeningReportCategory.value && Boolean(reportData.value);
    return (
      canAccessAnyBrandData ||
      canAccessAnyCompetitorData ||
      canAccessAnyCreatorData ||
      canAccessAnyListeningData
    );
  });

  const showGoalChip = computed(() => Boolean(reportMeta.value.goals?.overall));

  return {
    aggregateBy,
    availableAssignees,
    secondaryAggregateBy,
    boardFilterIds,
    brandIds,
    brandTagIds,
    brandTags,
    campaignFilterIds,
    canAccessReportData,
    channels,
    competitorTagIdsByBrand,
    competitorTagsByBrand,
    contentTagIds,
    contentTags,
    creatorIds,
    currency,
    customMetricKey,
    customMetricName,
    dashboardId,
    displayStrategy,
    formattedReportDataForTableAndGraphReports,
    filteredCompetitors,
    firstChannel,
    firstMetric,
    hasMediaSubtypes,
    hasUGCMetrics,
    customMetricDetails,
    inaccessibleContentTagIds,
    isCustomMetricReportTile,
    isFacebookCompetitiveReport,
    isFacebookReport,
    isInstagramCompetitiveReport,
    isInstagramReport,
    isMonetaryMetric,
    isMultiBrand,
    isMultiBrandOrCompetitiveReport,
    isMultiChannel,
    isMultiMetric,
    isReport,
    isStoryFrameReport,
    isStoryGalleryReport,
    isUGCMetric,
    isUGCChannel,
    metricReportRows,
    metrics,
    metricTypeReportKey,
    reportAdCampaigns,
    reportBenchmarksMetrics,
    reportBrandAggregated,
    reportBrandId,
    reportBrandIds,
    reportBrands,
    reportBrandsMetrics,
    reportCategory,
    reportChannel,
    reportChannels,
    reportChannelsMetrics,
    reportCreators,
    reportCreatorMetrics,
    reportCompetitorAverageMetrics,
    reportCompetitors,
    reportCompetitorIds,
    reportCompetitorsMetrics,
    reportCustomName,
    reportData,
    reportError,
    reportFilters,
    reportGroupedByMetrics,
    reportHasData,
    reportId,
    reportMediaTypeMetrics,
    reportMeta,
    reportMetric,
    reportMetricDetails,
    reportMetricNumbers,
    reportSortOrder,
    reportType,
    reportIsGroupedByBrand,
    showDownloadReportImageButton,
    sourceCampaignIds,
    showGoalChip,
    thumbnailMode,
    topicComparisonFilterIds,
    topicFilterIds,
    visionAiImageDownloadButtonTooltip,
    visionAiReport,
    canAccessData,
    isTimeSeriesReport,
  };
}
